import type {ColorStop, GradientOptions} from '@postermywall/fabricjs-2';
import {Gradient} from '@postermywall/fabricjs-2';
import type {GradientFillObject, GradientStop, LinearGradientFillObject, RadialGradientFillObject} from '@PosterWhiteboard/classes/fill.types';
import {FillTypes, RadialGradientShape} from '@PosterWhiteboard/classes/fill.types';
import {rgbaToRGBAString} from '@Utils/color.util';
import {assertNever} from '@Utils/assert-never.util';
import {degreesToRadians} from '@Utils/math.util';

/** The pixel-space rectangle a gradient is laid over, in the coordinate space of the Fabric object it fills. */
export interface GradientBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const createFabricGradient = (fill: GradientFillObject, box: GradientBox): Gradient<'linear'> | Gradient<'radial'> => {
  switch (fill.fillType) {
    case FillTypes.LINEAR_GRADIENT:
      return createLinearFabricGradient(fill, box);
    case FillTypes.RADIAL_GRADIENT:
      return createRadialFabricGradient(fill, box);
    default:
      return assertNever(fill);
  }
};

/**
 * Lays the gradient line through the box center along the angle, long enough that the 0 and 1 stops touch the box's far corners — the same line
 * CSS `linear-gradient(<angle>deg, ...)` draws.
 */
const createLinearFabricGradient = (fill: LinearGradientFillObject, box: GradientBox): Gradient<'linear'> => {
  const angleInRadians = degreesToRadians(fill.angle);
  const directionX = Math.sin(angleInRadians);
  const directionY = -Math.cos(angleInRadians);
  const halfLength = (Math.abs(box.width * directionX) + Math.abs(box.height * directionY)) / 2;
  const centerX = box.left + box.width / 2;
  const centerY = box.top + box.height / 2;

  const options: GradientOptions<'linear'> = {
    type: 'linear',
    colorStops: toFabricColorStops(fill.stops),
    coords: {
      x1: centerX - directionX * halfLength,
      y1: centerY - directionY * halfLength,
      x2: centerX + directionX * halfLength,
      y2: centerY + directionY * halfLength,
    },
  };
  return new Gradient(options);
};

const createRadialFabricGradient = (fill: RadialGradientFillObject, box: GradientBox): Gradient<'radial'> => {
  if (fill.shape === RadialGradientShape.ELLIPSE && box.width > 0 && box.height > 0) {
    return createEllipticalFabricGradient(fill, box);
  }
  const centerX = box.left + fill.center.x * box.width;
  const centerY = box.top + fill.center.y * box.height;

  const options: GradientOptions<'radial'> = {
    type: 'radial',
    colorStops: toFabricColorStops(fill.stops),
    coords: {
      x1: centerX,
      y1: centerY,
      r1: 0,
      x2: centerX,
      y2: centerY,
      r2: fill.radius * Math.max(box.width, box.height),
    },
  };
  return new Gradient(options);
};

/**
 * An ellipse whose axes are fractions of the box's own sides: a circle with the horizontal radius, squeezed vertically by the box's aspect
 * ratio. The squeeze is a transform about the object's origin, so the circle's center is placed where the squeeze moves it onto the box's.
 */
const createEllipticalFabricGradient = (fill: RadialGradientFillObject, box: GradientBox): Gradient<'radial'> => {
  const verticalScale = box.height / box.width;
  const centerX = box.left + fill.center.x * box.width;
  const centerY = (box.top + fill.center.y * box.height) / verticalScale;

  const options: GradientOptions<'radial'> = {
    type: 'radial',
    colorStops: toFabricColorStops(fill.stops),
    coords: {
      x1: centerX,
      y1: centerY,
      r1: 0,
      x2: centerX,
      y2: centerY,
      r2: fill.radius * box.width,
    },
    gradientTransform: [1, 0, 0, verticalScale, 0, 0],
  };
  return new Gradient(options);
};

export const toFabricColorStops = (stops: GradientStop[]): ColorStop[] => {
  return stops.map((stop) => {
    return {offset: stop.offset, color: rgbaToRGBAString(stop.color)};
  });
};
